// historyService.js - Avec gestion des sessions de conversation + timeout 30 min
import { db } from '../firebase';
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  deleteDoc,
  doc,
  writeBatch,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Clé pour stocker le sessionId actuel
const SESSION_KEY = 'medavise_current_session';

// ⏱️ Timeout en millisecondes (30 minutes)
const SESSION_TIMEOUT = 30 * 60 * 1000;

export const historyService = {
  /**
   * 🆕 Génère ou récupère le sessionId actuel (avec timeout 30 min)
   */
  getCurrentSessionId() {
    try {
      const data = sessionStorage.getItem(SESSION_KEY);

      if (data) {
        const parsed = JSON.parse(data);
        const { sessionId, lastActivity } = parsed;
        const now = Date.now();

        // ⏱️ Si actif dans les 30 dernières minutes, garder la session
        if (now - lastActivity < SESSION_TIMEOUT) {
          // Mettre à jour lastActivity
          sessionStorage.setItem(
            SESSION_KEY,
            JSON.stringify({
              sessionId,
              lastActivity: now,
            })
          );
          return sessionId;
        }
      }

      // Sinon, nouvelle session
      return this.startNewSession();
    } catch (error) {
      console.error('Erreur getCurrentSessionId:', error);
      return this.startNewSession();
    }
  },

  /**
   * Réinitialise la session (nouvelle conversation)
   */
  startNewSession() {
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const sessionData = {
      sessionId: newSessionId,
      lastActivity: Date.now(),
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
    return newSessionId;
  },

  /**
   * 🆕 Met à jour le timestamp d'activité (appelé à chaque message)
   */
  updateActivity() {
    try {
      const data = sessionStorage.getItem(SESSION_KEY);
      if (data) {
        const parsed = JSON.parse(data);
        parsed.lastActivity = Date.now();
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(parsed));
      }
    } catch (error) {
      console.error('Erreur updateActivity:', error);
    }
  },

  /**
   * Sauvegarde une conversation via la Cloud Function (chiffrement AES-256-GCM)
   */
  async saveConversation(userId, userMessage, aiResponse) {
    try {
      if (!userId) {
        console.warn('⚠️ Utilisateur non connecté - historique local');
        return this.saveToLocalStorage(userMessage, aiResponse);
      }

      const sessionId = this.getCurrentSessionId();

      const functions = getFunctions(undefined, 'europe-west1');
      const saveChatMessage = httpsCallable(functions, 'saveChatMessage');
      const result = await saveChatMessage({
        userMessage,
        aiResponse,
        conversationId: sessionId,
      });

      this.updateActivity();

      return result.data?.docId || null;
    } catch (error) {
      console.error('❌ Erreur saveChatMessage CF, fallback localStorage:', error);
      return this.saveToLocalStorage(userMessage, aiResponse);
    }
  },

  /**
   * Récupère l'historique GROUPÉ par session
   */
  async getHistoryGrouped(userId) {
    try {
      if (!userId) {
        return this.getFromLocalStorageGrouped();
      }

      const functions = getFunctions(undefined, 'europe-west1');
      const getChatHistory = httpsCallable(functions, 'getChatHistory');
      const result = await getChatHistory({ limit: 200 });
      const messages = result.data?.messages || [];

      const messagesMap = new Map();

      messages.forEach((msg) => {
        const sessionId = msg.conversationId || msg.sessionId || msg.id;
        const msgTime = msg.createdAt ? new Date(msg.createdAt) : new Date();

        if (!messagesMap.has(sessionId)) {
          messagesMap.set(sessionId, {
            sessionId: sessionId,
            messages: [],
            firstTimestamp: msgTime,
            lastTimestamp: msgTime,
          });
        }

        const session = messagesMap.get(sessionId);
        session.messages.push({
          id: msg.id,
          userMessage: msg.userMessage,
          aiResponse: msg.aiResponse,
          timestamp: msgTime,
        });

        if (msgTime < session.firstTimestamp) {
          session.firstTimestamp = msgTime;
        }
        if (msgTime > session.lastTimestamp) {
          session.lastTimestamp = msgTime;
        }
      });

      const sessions = Array.from(messagesMap.values()).map((session) => ({
        ...session,
        messages: session.messages.sort((a, b) => a.timestamp - b.timestamp),
        summary:
          session.messages[0]?.userMessage && typeof session.messages[0].userMessage === 'string'
            ? session.messages[0].userMessage.substring(0, 50) + '...'
            : 'Conversation',
      }));

      sessions.sort((a, b) => b.lastTimestamp - a.lastTimestamp);

      return sessions;
    } catch (error) {
      console.error('❌ Erreur getChatHistory CF:', error);
      return this.getFromLocalStorageGrouped();
    }
  },

  /**
   * Récupère l'historique (ancienne méthode pour compatibilité)
   */
  async getHistory(userId) {
    try {
      if (!userId || !db) {
        return this.getFromLocalStorage();
      }

      const q = query(
        collection(db, 'chatHistory'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const history = [];

      querySnapshot.forEach((document) => {
        const data = document.data();
        history.push({
          id: document.id,
          chat_id: document.id,
          sessionId: data.sessionId || document.id,
          user_message: data.userMessage,
          ai_response: data.aiResponse,
          timestamp: data.timestamp?.toDate() || new Date(data.createdAt),
          userMessage: data.userMessage,
          aiResponse: data.aiResponse,
        });
      });

      if (history.length === 0) {
        const localHistory = this.getFromLocalStorage();
        return localHistory;
      }

      return history;
    } catch (error) {
      console.error('❌ Erreur Firestore, fallback localStorage:', error);
      return this.getFromLocalStorage();
    }
  },

  /**
   * Fallback localStorage
   */
  saveToLocalStorage(userMessage, aiResponse) {
    try {
      const history = JSON.parse(localStorage.getItem('medavise_chat_history') || '[]');
      const sessionId = this.getCurrentSessionId();

      const newEntry = {
        id: `local_${Date.now()}`,
        sessionId: sessionId,
        user_message: userMessage,
        ai_response: aiResponse,
        timestamp: new Date().toISOString(),
        userMessage: userMessage,
        aiResponse: aiResponse,
        source: 'localStorage',
      };

      history.unshift(newEntry);

      if (history.length > 50) {
        history.length = 50;
      }

      localStorage.setItem('medavise_chat_history', JSON.stringify(history));

      // Mettre à jour l'activité
      this.updateActivity();

      return newEntry.id;
    } catch (error) {
      console.error('❌ Erreur localStorage:', error);
      return null;
    }
  },

  /**
   * Récupération localStorage
   */
  getFromLocalStorage() {
    try {
      const history = JSON.parse(localStorage.getItem('medavise_chat_history') || '[]');

      return history
        .map((item) => ({
          id: item.id,
          chat_id: item.id,
          sessionId: item.sessionId || item.id,
          user_message: item.user_message || item.userMessage,
          ai_response: item.ai_response || item.aiResponse,
          timestamp: new Date(item.timestamp),
          userMessage: item.user_message || item.userMessage,
          aiResponse: item.ai_response || item.aiResponse,
        }))
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    } catch (error) {
      console.error('❌ Erreur lecture localStorage:', error);
      return [];
    }
  },

  /**
   * Récupération localStorage GROUPÉE
   */
  getFromLocalStorageGrouped() {
    try {
      const history = this.getFromLocalStorage();
      const sessionsMap = new Map();

      history.forEach((item) => {
        const sessionId = item.sessionId || item.id;

        if (!sessionsMap.has(sessionId)) {
          sessionsMap.set(sessionId, {
            sessionId: sessionId,
            messages: [],
            firstTimestamp: item.timestamp,
            lastTimestamp: item.timestamp,
          });
        }

        const session = sessionsMap.get(sessionId);
        session.messages.push({
          id: item.id,
          userMessage: typeof item.userMessage === 'string' ? item.userMessage : '[Message chiffré]',
          aiResponse: typeof item.aiResponse === 'string' ? item.aiResponse : '[Réponse chiffrée]',
          timestamp: item.timestamp,
        });
      });

      const sessions = Array.from(sessionsMap.values()).map((session) => ({
        ...session,
        messages: session.messages.sort((a, b) => a.timestamp - b.timestamp),
        summary:
          session.messages[0]?.userMessage && typeof session.messages[0].userMessage === 'string'
            ? session.messages[0].userMessage.substring(0, 50) + '...'
            : 'Conversation',
      }));

      sessions.sort((a, b) => b.lastTimestamp - a.lastTimestamp);

      return sessions;
    } catch (error) {
      console.error('❌ Erreur lecture localStorage groupé:', error);
      return [];
    }
  },

  /**
   * Supprime une conversation (un message)
   */
  async deleteConversation(conversationId, userId) {
    try {
      if (conversationId.startsWith('local_')) {
        const history = JSON.parse(localStorage.getItem('medavise_chat_history') || '[]');
        const filtered = history.filter((item) => item.id !== conversationId);
        localStorage.setItem('medavise_chat_history', JSON.stringify(filtered));
        return true;
      }

      if (db && userId) {
        await deleteDoc(doc(db, 'chatHistory', conversationId));
        return true;
      }

      console.warn('⚠️ Impossible de supprimer:', conversationId);
      return false;
    } catch (error) {
      console.error('❌ Erreur suppression:', error);
      return false;
    }
  },

  /**
   * Supprime une SESSION entière (tous les messages de la session)
   */
  async deleteSession(sessionId, userId) {
    try {
      if (!db || !userId) {
        // Suppression locale
        const history = JSON.parse(localStorage.getItem('medavise_chat_history') || '[]');
        const filtered = history.filter((item) => item.sessionId !== sessionId);
        localStorage.setItem('medavise_chat_history', JSON.stringify(filtered));
        return true;
      }

      // Récupérer tous les messages de cette session
      const q = query(
        collection(db, 'chatHistory'),
        where('userId', '==', userId),
        where('sessionId', '==', sessionId)
      );

      const snapshot = await getDocs(q);

      // Supprimer en batch
      const batch = writeBatch(db);
      snapshot.docs.forEach((document) => {
        batch.delete(doc(db, 'chatHistory', document.id));
      });

      await batch.commit();

      return true;
    } catch (error) {
      console.error('❌ Erreur suppression session:', error);
      return false;
    }
  },

  /**
   * Supprime TOUT l'historique d'un utilisateur
   */
  async clearAllHistory(userId) {
    try {
      let deletedCount = 0;

      if (db && userId) {
        const q = query(collection(db, 'chatHistory'), where('userId', '==', userId));

        const snapshot = await getDocs(q);

        const batch = writeBatch(db);
        snapshot.docs.forEach((document) => {
          batch.delete(doc(db, 'chatHistory', document.id));
        });

        await batch.commit();
        deletedCount += snapshot.size;
      }

      const localHistory = JSON.parse(localStorage.getItem('medavise_chat_history') || '[]');
      deletedCount += localHistory.length;
      localStorage.removeItem('medavise_chat_history');

      // Reset session
      sessionStorage.removeItem(SESSION_KEY);

      return deletedCount;
    } catch (error) {
      console.error('❌ Erreur suppression complète:', error);
      throw error;
    }
  },
};
